import React, { useState, useEffect, useRef } from 'react';
import { Nav } from '../../components/nav';
import { useAuth } from '../../navigation/AuthProvider';
import {
  create_new_customer,
  delete_customer,
  edit_customer
} from '../../utils/customerRequests';
import '../css/Customers.css';
import { Toast } from '../../components/ToastPopup';
import { getPermission } from '../../permissions/rolePermissions';


export const Customers = () => {
  const { company, customers, setCustomers, user, setProjects } = useAuth();

  const employeesPermission = getPermission(user.role, "customers");
  const canEditCustomers = employeesPermission === "edit";
  
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  const [allCustomers, setAllCustomers] = useState(customers || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [customerName, setCustomerName] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');

  const [activeMenu, setActiveMenu] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    setFilteredCustomers(
      allCustomers.filter((customer) =>
        customer.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, allCustomers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
    clearForm();
  };

  const handleEdit = (customer) => {
    setActiveMenu(null);
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setCustomerMessage(customer.message);
    setIsPopupOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newCustomer = { name: customerName, message: customerMessage };
    const data = selectedCustomer
      ? await edit_customer({
          user,
          company,
          customer: selectedCustomer,
          newCustomerParams: newCustomer
        })
      : await create_new_customer({
          user,
          company,
          newCustomerParams: newCustomer
        });

    if (data.status === 'success') {
      setCustomers(data.customers);
      setAllCustomers(data.customers);
      clearForm();
      setToastType("success");
      setToastMessage(selectedCustomer ? "Kund redigerad!" : "Kund tillagd!");
      setIsPopupOpen(false);
    } else {
      setToastType("error");
      setToastMessage(`Kunde inte spara kund: ${data.errorText}`);
    }
  };

  const handleDelete = async (customer) => {
    if (window.confirm('Är du säker på att du vill radera kunden?')) {
      const data = await delete_customer({ customer, company, user });
      if (data && data.status === 'success') {
        setCustomers(data.customers);
        setAllCustomers(data.customers);
        setProjects(data.projects);
        setToastType("success");
        setToastMessage("Kund raderad!");
      } else {
        setToastType("error");
        setToastMessage(`Kunde inte ta bort kund: ${data.errorText}`);
      }
    }
  };

  const clearForm = () => {
    setCustomerName('');
    setCustomerMessage('');
    setSelectedCustomer(null);
  };

  const toggleMenu = (customerId) => {
    setActiveMenu(activeMenu === customerId ? null : customerId);
  };

  return (
    <div className="customers-page">
      <Nav />
      <div className="page-header">
        <h1>Kunder</h1>
        {canEditCustomers && (
          <button className="add-button" onClick={togglePopup}>
            Lägg till kund
          </button>
        )}
      </div>

      <input
        type="text"
        className="search-input"
        placeholder="sök kund"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {isPopupOpen && (
        <div
          className="project-popup-overlay active"
          onClick={() => setIsPopupOpen(false)}
        >
          <div className="popup" onClick={(e) => e.stopPropagation()}>
            <h4>
              {selectedCustomer ? 'Redigera kund' : 'Lägg till ny kund'}
            </h4>
            <form onSubmit={handleSubmit} className="customer-form">
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Kundens namn"
                required
              />
              <textarea
                value={customerMessage}
                maxLength={60}
                onChange={(e) => setCustomerMessage(e.target.value)}
                placeholder="Anteckning om kunden (valfritt)"
              />
              <button type="submit" className="create-button">
                {selectedCustomer ? 'Spara ändringar' : 'Skapa kund'}
              </button>
              <button
                type="button"
                className="close-button"
                onClick={() => setIsPopupOpen(false)}
              >
                Stäng
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="customers-list">
        <div className="column-headers">
          <p className="column-header name-header">Namn</p>
          <p className="column-header message-header">Anteckning</p>
          <div className="column-action-placeholder" />
        </div>

        {filteredCustomers.map((customer) => (
          <div className="customer-row" key={customer._id}>
            <p className="customer-name">{customer.name}</p>
            <p className="customer-message">{customer.message}</p>

            <div
              className={`customer-actions ${
                activeMenu === customer._id ? 'active' : ''
              }`}
              ref={activeMenu === customer._id ? menuRef : null}
            >
              {canEditCustomers && (
                <button
                  className="menu-button"
                  onClick={() => toggleMenu(customer._id)}
                >
                  ⋮
                </button>
              )}
              {activeMenu === customer._id && (
                <div className="action-menu">
                  <button onClick={() => handleEdit(customer)}>
                    Redigera
                  </button>
                  <button onClick={() => handleDelete(customer)}>
                    Radera
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Toast
        message={toastMessage}
        type={toastType}
        onClose={() => setToastMessage("")}
      />
    </div>
  );
};

export default Customers;
